/**
 * Global "please wait" overlay. Anything slow - saving, deleting, uploading,
 * building an Excel file - registers a task here and <GlobalLoader /> shows a
 * backdrop with a spinner while at least one task is running.
 *
 *   const task = startLoading('Uploading master…');
 *   task.update('Uploading part 2 of 5…');
 *   task.done();
 *
 *   await withLoader('Preparing Excel…', () => buildWorkbook());
 *
 * Every POST / PUT / DELETE through services/api.js registers an automatic
 * task ("Saving…", "Deleting…"); explicit tasks win over automatic ones, so
 * a multi-part upload shows its own progress message.
 */
let nextId = 1;
const tasks = new Map(); // id -> { message, detail, auto, startedAt }
const listeners = new Set();

function emit() {
  const snapshot = currentState();
  listeners.forEach((listener) => listener(snapshot));
}

/** The task shown: the newest explicit task, else the newest automatic one. */
export function currentState() {
  const list = [...tasks.values()];
  const explicit = list.filter((t) => !t.auto);
  const pool = explicit.length ? explicit : list;
  const shown = pool[pool.length - 1] || null;
  return {
    active: list.length > 0,
    message: shown?.message || '',
    detail: shown?.detail || '',
    startedAt: list.reduce((min, t) => Math.min(min, t.startedAt), Infinity),
  };
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function startLoading(message = 'Please wait…', { auto = false, detail = '' } = {}) {
  const id = nextId;
  nextId += 1;
  tasks.set(id, { message, detail, auto, startedAt: Date.now() });
  emit();
  return {
    update(nextMessage, nextDetail) {
      const task = tasks.get(id);
      if (!task) return;
      if (nextMessage) task.message = nextMessage;
      if (nextDetail !== undefined) task.detail = nextDetail;
      emit();
    },
    done() {
      if (tasks.delete(id)) emit();
    },
  };
}

/** Runs fn (or awaits a promise) with the overlay shown; returns its result. */
export async function withLoader(message, fn, options) {
  const task = startLoading(message, options);
  try {
    return await (typeof fn === 'function' ? fn(task) : fn);
  } finally {
    task.done();
  }
}
